import { z } from "zod"
import { ProductSchema } from "./catalog"
import { ShopCustomOrderSettingsSchema } from "./shop-custom-order"

export const ShopStatusSchema = z.enum(["active", "blocked"])
export type ShopStatus = z.infer<typeof ShopStatusSchema>

export const BannerSlideSchema = z.object({
  id: z.string(),
  imageUrl: z.string().optional(),
  title: z.string().optional(),
  description: z.string().optional(),
  ctaUrl: z.string().optional(),
})
export type BannerSlide = z.infer<typeof BannerSlideSchema>

export const AboutBlockKindSchema = z.enum([
  "title-page",
  "cards-6",
  "hover-animation",
  "photo-gallery",
  "large-photo",
  "gallery",
  // fd-boutique-08: информационные блоки «О компании» — история-таймлайн,
  // три преимущества, процесс изготовления. Пункты — в `cards`.
  "timeline",
  "advantages",
  "process",
])
export type AboutBlockKind = z.infer<typeof AboutBlockKindSchema>

export const AboutBlockSchema = z.object({
  id: z.string(),
  kind: AboutBlockKindSchema,
  enabled: z.boolean(),
  title: z.string().optional(),
  description: z.string().optional(),
  cards: z.array(
    z.object({
      id: z.string(),
      imageUrl: z.string().optional(),
      subtitle: z.string().optional(),
      description: z.string().optional(),
      // Год события таймлайна или номер шага процесса.
      label: z.string().optional(),
    }),
  ),
  images: z.array(z.string()),
})
export type AboutBlock = z.infer<typeof AboutBlockSchema>

export const InteractiveBlockSchema = z.discriminatedUnion("type", [
  z.object({
    id: z.string(),
    type: z.literal("interior-pins"),
    slides: z.array(
      z.object({
        id: z.string(),
        imageUrl: z.string(),
        pins: z.array(
          z.object({
            id: z.string(),
            x: z.number(),
            y: z.number(),
            productId: z.string(),
          }),
        ),
      }),
    ),
  }),
  z.object({
    id: z.string(),
    type: z.literal("outfit-gallery"),
    outfits: z.array(
      z.object({
        id: z.string(),
        imageUrl: z.string(),
        productIds: z.array(z.string()),
      }),
    ),
  }),
  z.object({
    id: z.string(),
    type: z.literal("banner"),
    slides: z.array(BannerSlideSchema),
  }),
  z.object({
    id: z.string(),
    type: z.literal("about-brand"),
    heading: z.string(),
    body: z.string(),
    photoUrl: z.string().optional(),
  }),
])
export type InteractiveBlock = z.infer<typeof InteractiveBlockSchema>

export const BoutiqueCollectionRefSchema = z.object({
  id: z.string(),
  title: z.string(),
  productIds: z.array(z.string()),
  categoryIds: z.array(z.string()).optional(),
})
export type BoutiqueCollectionRef = z.infer<typeof BoutiqueCollectionRefSchema>

// fd-boutique-09: новость магазина, которую продавец пишет в конструкторе.
export const BoutiqueNewsItemSchema = z.object({
  id: z.string(),
  title: z.string(),
  body: z.string(),
  imageUrl: z.string().optional(),
  /** Дата новости, ГГГГ-ММ-ДД. */
  date: z.string(),
})
export type BoutiqueNewsItem = z.infer<typeof BoutiqueNewsItemSchema>

// fd-boutique-09: сезонная акция — обложка и подборка товаров со скидкой.
export const BoutiqueSeasonalSaleSchema = z.object({
  enabled: z.boolean(),
  title: z.string(),
  description: z.string().optional(),
  imageUrl: z.string().optional(),
})
export type BoutiqueSeasonalSale = z.infer<typeof BoutiqueSeasonalSaleSchema>

export const BoutiqueConfigSchema = z.object({
  header: z.object({
    logoUrl: z.string().optional(),
    description: z.string(),
    customOrdersEnabled: z.boolean(),
  }),
  about: z.object({
    enabled: z.boolean(),
    titlePageEnabled: z.boolean(),
    blocks: z.array(AboutBlockSchema),
  }),
  banner: z.object({
    enabled: z.boolean(),
    slides: z.array(BannerSlideSchema),
  }),
  pinnedProducts: z.array(z.string()),
  collections: z.object({
    groupByCollection: z.boolean(),
    items: z.array(BoutiqueCollectionRefSchema),
  }),
  partnerProducts: z.array(z.string()),
  interactiveBlocks: z.array(InteractiveBlockSchema),
  // fd-boutique-10: порядок блоков лендинга (ключи блоков).
  blockOrder: z.array(z.string()).optional(),
  // fd-boutique-09: новости магазина и сезонная акция. Опционально — старые
  // конфиги без полей валидны.
  news: z.array(BoutiqueNewsItemSchema).optional(),
  seasonalSale: BoutiqueSeasonalSaleSchema.optional(),
  // fd-boutique-13: прайс и способы оплаты индивидуального заказа.
  customOrder: ShopCustomOrderSettingsSchema.optional(),
  published: z.boolean(),
  updatedAt: z.string(),
})
export type BoutiqueConfig = z.infer<typeof BoutiqueConfigSchema>

export const ShopSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  tagline: z.string(),
  description: z.string(),
  logoUrl: z.string().optional(),
  heroUrl: z.string(),
  rating: z.number(),
  ratingCount: z.number(),
  productCount: z.number(),
  location: z.string(),
  since: z.string(),
  status: ShopStatusSchema,
  // Бейдж «Проверенный продавец» (fd-onboard-12): документы приняты, ИНН
  // проверен, продавец активен. Опционально — старые ответы витрины валидны.
  verified: z.boolean().optional(),
})
export type Shop = z.infer<typeof ShopSchema>

// fd-boutique-12: партнёрский магазин бутика — магазин, чьи товары продавец
// показывает в партнёрском блоке своего бутика.
export const BoutiquePartnerSchema = z.object({
  shopId: z.string(),
  slug: z.string(),
  name: z.string(),
  logoUrl: z.string().optional(),
  status: z.enum(["active"]),
  productCount: z.number().int().nonnegative(),
})
export type BoutiquePartner = z.infer<typeof BoutiquePartnerSchema>

// Магазин — кандидат в партнёры (доступен для выбора, ещё не добавлен).
export const BoutiquePartnerCandidateSchema = BoutiquePartnerSchema.omit({ status: true })
export type BoutiquePartnerCandidate = z.infer<typeof BoutiquePartnerCandidateSchema>

// Ответ GET /seller/boutique/partners: выбранные партнёры + доступные магазины.
export const BoutiquePartnerListSchema = z.object({
  partners: z.array(BoutiquePartnerSchema),
  available: z.array(BoutiquePartnerCandidateSchema),
})
export type BoutiquePartnerList = z.infer<typeof BoutiquePartnerListSchema>

// Тело PUT /seller/boutique/partners: полный список id магазинов-партнёров.
export const SetBoutiquePartnersRequestSchema = z.object({
  partnerShopIds: z.array(z.string()),
})
export type SetBoutiquePartnersRequest = z.infer<typeof SetBoutiquePartnersRequestSchema>

// Резолвнутый партнёрский товар для витрины: карточка товара + метка магазина —
// источника, чтобы фронт мог показать его в партнёрском блоке с атрибуцией.
export const PartnerProductSchema = z.object({
  product: ProductSchema,
  partnerShopId: z.string(),
  partnerShopName: z.string().optional(),
})
export type PartnerProduct = z.infer<typeof PartnerProductSchema>

export const StorefrontSchema = z.object({
  shop: ShopSchema,
  config: BoutiqueConfigSchema,
  // fd-boutique-12: резолвнутые товары партнёрского блока. Опционально —
  // старые витрины и витрины без партнёров возвращаются без поля.
  partnerCatalog: z.array(PartnerProductSchema).optional(),
  // fd-boutique-01: код ссылки магазина /s/<код> (если продавец её заводил) и
  // тариф магазина: «premium» — у магазина опубликован бутик из конструктора.
  shareCode: z.string().optional(),
  tier: z.enum(["premium"]).optional(),
})
export type Storefront = z.infer<typeof StorefrontSchema>

/** Admin storefront list item (same wire fields as Shop, named for UI clarity). */
export const StorefrontListItemSchema = ShopSchema
export type StorefrontListItem = Shop
